import { ChevronDown, ChevronUp, Plus } from "lucide-react"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import RoomAutomations from "./RoomAutomations"
import { ChatAutomationScope } from "@/lib/chatAutomations"
import { useRoomWorkflows } from "@/hooks/useRoomWorkflows"

type Props = {
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  /** Drop the card border/shadow and the collapse chevron, fill the parent —
   * used inside the tabbed widget panel. Header (title + Add) stays. */
  bare?: boolean
  // A stop automation has no dedicated creation form — it hands off to Slick
  // AI instead: switch the composer to AI mode and drop a fill-in-the-blanks
  // template in the textarea as the hint.
  onAddAutomation: (prompt: string) => void
  /** The room this card sits in — the card shows only the automations that
   * concern this driver and their truck. */
  scope: ChatAutomationScope
}

/**
 * Automations card whose header (title, count) stays visible even
 * when collapsed — only the list body hides. Used where the column's width
 * must stay reserved regardless of collapse state (the driver chat's
 * Tracking sidebar). WorkflowWidget's icon-only collapsed state is left
 * untouched for the Slick AI sidebar, which collapses its whole column
 * instead.
 */
const AutomationsCard = ({
  expanded,
  onExpandedChange,
  bare = false,
  onAddAutomation,
  scope,
}: Props) => {
  const { t } = useTranslation()
  const { count } = useRoomWorkflows(scope)
  const open = bare || expanded

  const onAddStopAutomation = () =>
    onAddAutomation(
      'Set up an automation for a stop: "[stop]". When [condition], [do this].',
    )

  return (
    <div
      className={cn(
        "flex w-full flex-col overflow-hidden",
        bare
          ? "h-full"
          : "rounded-lg border border-border bg-card shadow-[0_4px_20px_rgba(0,0,0,0.04)]",
        !open && "shrink-0",
      )}
    >
      <div
        className={cn(
          "flex h-12 shrink-0 items-center justify-between px-4",
          !bare && "cursor-pointer",
        )}
        onClick={bare ? undefined : () => onExpandedChange(!expanded)}
        role={bare ? undefined : "button"}
        tabIndex={bare ? undefined : 0}
        onKeyDown={
          bare
            ? undefined
            : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  onExpandedChange(!expanded)
                }
              }
        }
        aria-expanded={bare ? undefined : expanded}
        title={
          bare
            ? undefined
            : expanded
              ? t("ai.automations.hide")
              : t("ai.automations.show")
        }
      >
        <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          {t("ai.automations.title")}
          {count > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-lg bg-gray-200 px-1 text-[10px] font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-200">
              {count}
            </span>
          )}
        </h2>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            aria-label={t("ai.automations.add")}
            title={t("ai.automations.add")}
            onClick={(e) => {
              e.stopPropagation()
              onAddStopAutomation()
            }}
          >
            <Plus />
            {t("common.add")}
          </Button>
          {!bare &&
            (expanded ? (
              <ChevronUp className="h-4 w-4" strokeWidth={1.5} />
            ) : (
              <ChevronDown className="h-4 w-4" strokeWidth={1.5} />
            ))}
        </div>
      </div>
      {open && (
        <div
          className={cn(
            "overflow-y-auto border-t border-gray-100 dark:border-gray-800",
            bare ? "min-h-0 flex-1" : "max-h-[40vh]",
          )}
        >
          <RoomAutomations
            scope={scope}
            onAddStopAutomation={onAddStopAutomation}
          />
        </div>
      )}
    </div>
  )
}

export default AutomationsCard
